import React, { useState, useEffect } from 'react';
import { DictionaryControllerService } from '../api';
import { PreseasonGameResultControllerService } from '../api/services/PreseasonGameResultControllerService';
import './Results.css';

function Results() {
  const [territories, setTerritories] = useState([]);
  const [selectedVoivodeship, setSelectedVoivodeship] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Pobierz słownik terytoriów
  useEffect(() => {
    const fetchDictionaries = async () => {
      try {
        const data = await DictionaryControllerService.getDictionaries();
        setTerritories(data?.territories || []);
      } catch (error) {
        // error handled silently
      }
    };
    fetchDictionaries();
  }, []);

  const fetchResults = async (pageNum) => {
    setLoading(true);
    try {
      const response = await PreseasonGameResultControllerService.getResults(
        selectedVoivodeship || undefined,
        selectedCounty || undefined,
        dateFrom || undefined,
        dateTo || undefined,
        pageNum,
        150,
      );
      setResults(response?.content || []);
      setTotalPages(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);
    } catch (error) {
      setResults([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchResults(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchResults(newPage);
  };

  // Powiaty dla wybranego województwa
  const counties = selectedVoivodeship
    ? (territories.find(t => t.key === selectedVoivodeship)?.subValues || [])
    : [];

  const handleVoivodeshipChange = (e) => {
    setSelectedVoivodeship(e.target.value);
    setSelectedCounty('');
  };

  const handleCountyChange = (e) => {
    setSelectedCounty(e.target.value);
  };

  const handleExportTxt = () => {
    if (results.length === 0) return;
    const lines = results.map(r => {
      const time = (r.startTime?.slice(0, 5) || '') + (r.endTime ? ` - ${r.endTime.slice(0, 5)}` : '');
      const score = r.scoreCreator != null && r.scoreOpponent != null ? `${r.scoreCreator}:${r.scoreOpponent}` : '—';
      return `${r.teamCreatorName} ${score} ${r.teamOpponentName} | ${r.date} | ${time} | ${r.location || '—'}`;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sparingi.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="results-container">
      <h2>Sparingi</h2>

      <div className="results-filters">
        <div className="results-filter-group">
          <label>Województwo</label>
          <select value={selectedVoivodeship} onChange={handleVoivodeshipChange}>
            <option value="">Wszystkie</option>
            {territories.map(t => (
              <option key={t.key} value={t.key}>{t.displayValue}</option>
            ))}
          </select>
        </div>

        <div className="results-filter-group">
          <label>Powiat</label>
          <select
            value={selectedCounty}
            onChange={handleCountyChange}
            disabled={!selectedVoivodeship}
          >
            <option value="">Wszystkie</option>
            {counties.map(c => (
              <option key={c.key} value={c.key}>{c.displayValue}</option>
            ))}
          </select>
        </div>

        <div className="results-filter-group">
          <label>Data od</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>

        <div className="results-filter-group">
          <label>Data do</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <div className="results-search-btn-wrapper">
        <button className="results-search-btn" onClick={handleSearch} disabled={loading}>
          {loading ? 'Szukanie...' : 'Szukaj'}
        </button>
      </div>

      {results.length > 0 && !loading && (
        <div className="results-export">
          <button className="results-export-btn" onClick={handleExportTxt}>
            Eksportuj do .txt
          </button>
        </div>
      )}

      {loading ? (
        <div className="results-loading">Ładowanie...</div>
      ) : !hasSearched ? (
        <div className="results-empty">Kliknij "Szukaj", aby wyświetlić wyniki</div>
      ) : results.length === 0 ? (
        <div className="results-empty">Brak wyników do wyświetlenia</div>
      ) : (
        <>
          {/* Desktop: tabela */}
          <div className="results-table-wrapper">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Drużyna 1</th>
                  <th>Wynik</th>
                  <th>Drużyna 2</th>
                  <th>Data</th>
                  <th>Godzina</th>
                  <th>Lokalizacja</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.id}>
                    <td>{r.teamCreatorName}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>
                      {r.scoreCreator != null && r.scoreOpponent != null
                        ? `${r.scoreCreator} : ${r.scoreOpponent}`
                        : '—'}
                    </td>
                    <td>{r.teamOpponentName}</td>
                    <td>{r.date}</td>
                    <td>
                      {r.startTime?.slice(0, 5)}
                      {r.endTime ? ` - ${r.endTime.slice(0, 5)}` : ''}
                    </td>
                    <td>{r.location || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: karty */}
          <div className="results-cards">
            {results.map(r => (
              <div key={r.id} className="results-card">
                <div className="results-card-teams">
                  <span className="results-card-team">{r.teamCreatorName}</span>
                  <span className="results-card-score">
                    {r.scoreCreator != null && r.scoreOpponent != null
                      ? `${r.scoreCreator} : ${r.scoreOpponent}`
                      : 'vs'}
                  </span>
                  <span className="results-card-team">{r.teamOpponentName}</span>
                </div>
                <div className="results-card-details">
                  <span>{r.date}</span>
                  <span>
                    {r.startTime?.slice(0, 5)}
                    {r.endTime ? ` - ${r.endTime.slice(0, 5)}` : ''}
                  </span>
                  {r.location && <span>{r.location}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="results-pagination">
            <button disabled={page === 0} onClick={() => handlePageChange(page - 1)}>
              Poprzednia
            </button>
            <span className="results-pagination-info">
              Strona {page + 1} z {totalPages} ({totalElements} wyników)
            </span>
            <button disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>
              Następna
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Results;
